import { useEffect, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useLanguage, DEFAULT_LOCALE, type Locale } from './useLanguage';
import { detectStorefront } from './useStorefront';
import { rewriteForIT, rewriteObjectForIT } from '@/lib/rewriteForIT';
import { rewriteObjectForAL, shouldScrubForAL } from '@/lib/rewriteForAL';

/**
 * IT rewrite gate — active ONLY when the runtime storefront is 'it'
 * (bigfollow.it, IT render-host, ?__bf_sf=it). On CH (incl. the /it/
 * second-locale tree) and AT this returns false, so all apply*() helpers
 * behave byte-identically to today.
 */
function isITStorefront(): boolean {
  if (typeof window === 'undefined') return false;
  return detectStorefront() === 'it';
}

/**
 * AL rewrite gate — active ONLY on sf='al' and never on legal routes.
 * CH/AT/IT untouched.
 */
function isALStorefront(): boolean {
  if (typeof window === 'undefined') return false;
  return shouldScrubForAL(detectStorefront());
}

/** Applies the storefront-specific last-mile scrubber (IT or AL), else identity. */
function scrub<T>(obj: T): T {
  if (isITStorefront()) return rewriteObjectForIT(obj);
  if (isALStorefront()) return rewriteObjectForAL(obj);
  return obj;
}

/**
 * Fetches a product translation overlay for the current locale.
 * Returns null when locale is DE or no translation found.
 */
export function useProductTranslation(productId: string | undefined) {
  const { locale } = useLanguage();
  const [overlay, setOverlay] = useState<Record<string, any> | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!productId || locale === DEFAULT_LOCALE) {
      setOverlay(null);
      setLoading(false);
      return;
    }

    let active = true;
    setOverlay(null);
    setLoading(true);

    supabase
      .from('product_translations')
      .select('*')
      .eq('product_id', productId)
      .eq('locale', locale)
      .maybeSingle()
      .then(
        ({ data }) => {
          if (!active) return;
          setOverlay(data ?? null);
          setLoading(false);
        },
        () => {
          if (!active) return;
          setOverlay(null);
          setLoading(false);
        }
      );

    return () => {
      active = false;
    };
  }, [productId, locale]);

  return { overlay, loading };
}

/**
 * Applies translation overlay to a product object.
 * Only overrides fields that have non-empty values in the translation.
 */
export function applyProductTranslation(product: any, overlay: Record<string, any> | null) {
  if (!product) return product;
  if (!overlay) {
    // IT storefront: even without a translation overlay, strip CH-only wording
    // (TWINT/Svizzera/BigFollow.ch) from any raw fields that leak through.
    return scrub(product);
  }
  const fields = [
    'title', 'h1_title', 'short_description', 'description',
    'meta_title', 'meta_description', 'input_label', 'input_placeholder',
    'delivery_time', 'gift_label', 'type_selector_label', 'variation_selector_label',
    'image_alt', 'image_title', 'slug',
  ];
  const merged = { ...product };
  for (const f of fields) {
    if (overlay[f] && overlay[f].trim()) {
      merged[f] = overlay[f];
    }
  }
  // rich_description is JSON – use translated version if available,
  // otherwise REMOVE the German rich_description so the translated
  // plain description is shown instead
  if (overlay.rich_description) {
    merged.rich_description = overlay.rich_description;
  } else {
    merged.rich_description = null;
  }
  return scrub(merged);
}

/**
 * Fetches a category translation overlay.
 */
export function useCategoryTranslation(categoryId: string | undefined) {
  const { locale } = useLanguage();
  const [overlay, setOverlay] = useState<Record<string, any> | null>(null);

  useEffect(() => {
    if (!categoryId || locale === DEFAULT_LOCALE) {
      setOverlay(null);
      return;
    }
    let active = true;
    supabase
      .from('category_translations')
      .select('*')
      .eq('category_id', categoryId)
      .eq('locale', locale)
      .maybeSingle()
      .then(({ data }) => {
        if (active && data) setOverlay(data);
      });
    return () => { active = false; };
  }, [categoryId, locale]);

  return overlay;
}

export function applyCategoryTranslation(category: any, overlay: Record<string, any> | null) {
  if (!category) return category;
  if (!overlay) {
    return scrub(category);
  }
  const merged = { ...category };
  if (overlay.name?.trim()) merged.name = overlay.name;
  if (overlay.meta_title?.trim()) merged.meta_title = overlay.meta_title;
  if (overlay.meta_description?.trim()) merged.meta_description = overlay.meta_description;
  return scrub(merged);
}

/**
 * Fetches a blog post translation overlay.
 */
export function useBlogPostTranslation(postId: string | undefined) {
  const { locale } = useLanguage();
  const [overlay, setOverlay] = useState<Record<string, any> | null>(null);

  useEffect(() => {
    if (!postId || locale === DEFAULT_LOCALE) {
      setOverlay(null);
      return;
    }
    let active = true;
    supabase
      .from('blog_post_translations')
      .select('*')
      .eq('post_id', postId)
      .eq('locale', locale)
      .maybeSingle()
      .then(({ data }) => {
        if (active && data) setOverlay(data);
      });
    return () => { active = false; };
  }, [postId, locale]);

  return overlay;
}

export function applyBlogPostTranslation(post: any, overlay: Record<string, any> | null) {
  if (!post) return post;
  if (!overlay) {
    return scrub(post);
  }
  const merged = { ...post };
  const fields = ['title', 'content', 'excerpt', 'meta_title', 'meta_description', 'image_alt', 'image_title'];
  for (const f of fields) {
    if (overlay[f] && overlay[f].trim()) {
      merged[f] = overlay[f];
    }
  }
  return scrub(merged);
}

/**
 * Batch-fetch blog post translations for a list of post IDs.
 */
export function useBlogPostsTranslations(postIds: string[]) {
  const { locale } = useLanguage();
  const [overlays, setOverlays] = useState<Record<string, Record<string, any>>>({});

  useEffect(() => {
    if (locale === DEFAULT_LOCALE || postIds.length === 0) {
      setOverlays({});
      return;
    }
    let active = true;
    supabase
      .from('blog_post_translations')
      .select('*')
      .eq('locale', locale)
      .in('post_id', postIds)
      .then(({ data }) => {
        if (active && data) {
          const map: Record<string, Record<string, any>> = {};
          for (const row of data) {
            map[row.post_id] = row;
          }
          setOverlays(map);
        }
      });
    return () => { active = false; };
  }, [locale, postIds.join(',')]);

  return overlays;
}

/**
 * Batch-fetch category translations for homepage.
 */
export function useCategoryTranslations(categoryIds: string[]) {
  const { locale } = useLanguage();
  const [overlays, setOverlays] = useState<Record<string, Record<string, any>>>({});

  useEffect(() => {
    if (locale === DEFAULT_LOCALE || categoryIds.length === 0) {
      setOverlays({});
      return;
    }
    let active = true;
    supabase
      .from('category_translations')
      .select('*')
      .eq('locale', locale)
      .in('category_id', categoryIds)
      .then(({ data }) => {
        if (active && data) {
          const map: Record<string, Record<string, any>> = {};
          for (const row of data) {
            map[row.category_id] = row;
          }
          setOverlays(map);
        }
      });
    return () => { active = false; };
  }, [locale, categoryIds.join(',')]);

  return overlays;
}

/**
 * Batch-fetch product translations for a list of products.
 */
export function useProductTranslations(productIds: string[]) {
  const { locale } = useLanguage();
  const [overlays, setOverlays] = useState<Record<string, Record<string, any>>>({});

  useEffect(() => {
    if (locale === DEFAULT_LOCALE || productIds.length === 0) {
      setOverlays({});
      return;
    }
    let active = true;
    supabase
      .from('product_translations')
      .select('*')
      .eq('locale', locale)
      .in('product_id', productIds)
      .then(({ data }) => {
        if (active && data) {
          const map: Record<string, Record<string, any>> = {};
          for (const row of data) {
            map[row.product_id] = row;
          }
          setOverlays(map);
        }
      });
    return () => { active = false; };
  }, [locale, productIds.join(',')]);

  return overlays;
}

/**
 * Batch-fetch product type translations for current locale.
 */
export function useProductTypeTranslations(typeIds: string[]) {
  const { locale } = useLanguage();
  const [overlays, setOverlays] = useState<Record<string, { label?: string; description?: string }>>({});
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (locale === DEFAULT_LOCALE || typeIds.length === 0) {
      setOverlays({});
      setLoading(false);
      return;
    }

    let active = true;
    setOverlays({});
    setLoading(true);

    supabase
      .from('product_type_translations' as any)
      .select('product_type_id, label, description')
      .eq('locale', locale)
      .in('product_type_id', typeIds)
      .then(
        ({ data }: any) => {
          if (!active) return;
          const map: Record<string, { label?: string; description?: string }> = {};
          for (const row of data || []) {
            map[row.product_type_id] = {
              ...(row.label?.trim() ? { label: row.label } : {}),
              ...(row.description?.trim() ? { description: row.description } : {}),
            };
          }
          setOverlays(map);
          setLoading(false);
        },
        () => {
          if (!active) return;
          setOverlays({});
          setLoading(false);
        }
      );

    return () => {
      active = false;
    };
  }, [locale, typeIds.join(',')]);

  return { overlays, loading };
}

/**
 * Batch-fetch product variation translations for current locale.
 */
export function useProductVariationTranslations(variationIds: string[]) {
  const { locale } = useLanguage();
  const [overlays, setOverlays] = useState<Record<string, string>>({});
  // Badge labels ("Beliebt") are localized separately from the package label.
  const [badgeOverlays, setBadgeOverlays] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (locale === DEFAULT_LOCALE || variationIds.length === 0) {
      setOverlays({});
      setBadgeOverlays({});
      setLoading(false);
      return;
    }

    let active = true;
    setOverlays({});
    setBadgeOverlays({});
    setLoading(true);

    supabase
      .from('product_variation_translations' as any)
      .select('product_variation_id, label, badge_label')
      .eq('locale', locale)
      .in('product_variation_id', variationIds)
      .then(
        ({ data }: any) => {
          if (!active) return;
          const map: Record<string, string> = {};
          const badges: Record<string, string> = {};
          for (const row of data || []) {
            if (row.label?.trim()) map[row.product_variation_id] = row.label;
            if (row.badge_label?.trim()) badges[row.product_variation_id] = row.badge_label;
          }
          setOverlays(map);
          setBadgeOverlays(badges);
          setLoading(false);
        },
        () => {
          if (!active) return;
          setOverlays({});
          setBadgeOverlays({});
          setLoading(false);
        }
      );

    return () => {
      active = false;
    };
  }, [locale, variationIds.join(',')]);

  return { overlays, badgeOverlays, loading };
}

/**
 * Batch-fetch product gift translations for current locale.
 */
export function useProductGiftTranslations(giftIds: string[]) {
  const { locale } = useLanguage();
  const [overlays, setOverlays] = useState<Record<string, Record<string, string>>>({});
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (locale === DEFAULT_LOCALE || giftIds.length === 0) {
      setOverlays({});
      setLoading(false);
      return;
    }

    let active = true;
    setOverlays({});
    setLoading(true);

    supabase
      .from('product_gift_translations' as any)
      .select('product_gift_id, label, input_label, input_placeholder')
      .eq('locale', locale)
      .in('product_gift_id', giftIds)
      .then(
        ({ data }: any) => {
          if (!active) return;
          const map: Record<string, Record<string, string>> = {};
          for (const row of data || []) {
            map[row.product_gift_id] = row;
          }
          setOverlays(map);
          setLoading(false);
        },
        () => {
          if (!active) return;
          setOverlays({});
          setLoading(false);
        }
      );

    return () => {
      active = false;
    };
  }, [locale, giftIds.join(',')]);

  return { overlays, loading };
}
